/**
 * DashboardCardTour — Feb 2026
 * ============================
 *
 * A ~30-second self-guided walkthrough of the teen's full dashboard.
 * Fires ONCE the first time a teen sees the un-simplified dashboard
 * (post 10-challenge unlock). Highlights each visible optional card
 * in turn with a portal-mounted spotlight + description bubble drawn
 * from the OPTIONAL_CARDS registry.
 *
 * Completion state is stored in localStorage under
 * `hibff_dashboard_tour_v1_done` so the tour never fires twice for
 * the same teen — but the version suffix lets us re-run it if we
 * meaningfully change the card set later.
 *
 * Skip / Next / Done are the only controls. The tour never blocks
 * navigation — a click on the underlying dashboard also ends the
 * tour (permissive dismiss).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, X, Sparkles } from 'lucide-react';

const TOUR_KEY = 'hibff_dashboard_tour_v1_done';

const DashboardCardTour = ({ cardOrder, cardMeta, enabled }) => {
  // `cardOrder` — array of visible optional card ids in render order
  // `cardMeta` — { [id]: { title, description } }  from OPTIONAL_CARDS
  // `enabled`  — parent already checked !simplified_dashboard etc.
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  const shouldRun = useMemo(() => {
    if (!enabled) return false;
    if (!cardOrder || !cardOrder.length) return false;
    try {
      return localStorage.getItem(TOUR_KEY) !== '1';
    } catch { return false; }
  }, [enabled, cardOrder]);

  // Kick off after cards have rendered.
  useEffect(() => {
    if (!shouldRun) return undefined;
    const t = setTimeout(() => setActive(true), 900);
    return () => clearTimeout(t);
  }, [shouldRun]);

  const finish = useCallback(() => {
    setActive(false);
    try { localStorage.setItem(TOUR_KEY, '1'); } catch (e) { /* noop */ }
  }, []);

  const currentId = cardOrder[step];
  const meta = currentId ? cardMeta[currentId] : null;

  // Recompute the highlighted card's bounding rect on every step +
  // on window resize, so the spotlight stays glued to the DOM.
  useEffect(() => {
    if (!active || !currentId) return undefined;
    const compute = () => {
      const el = document.querySelector(`[data-testid="optional-card-${currentId}"]`);
      if (!el) { setRect(null); return; }
      // Scroll into view when the tour moves to the next card.
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      // Wait a tick for the scroll before measuring.
      setTimeout(() => {
        const r = el.getBoundingClientRect();
        setRect({
          top: r.top + window.scrollY,
          left: r.left + window.scrollX,
          width: r.width,
          height: r.height,
        });
      }, 380);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [active, currentId]);

  if (!active || !meta || !rect) return null;

  const isLast = step >= cardOrder.length - 1;

  // Bubble sits ABOVE the card by default; if the card is near the
  // top of the viewport, flip it BELOW to stay on-screen.
  const bubbleAbove = (rect.top - window.scrollY) > 240;
  const bubbleStyle = bubbleAbove
    ? { top: rect.top - 168, left: rect.left }
    : { top: rect.top + rect.height + 12, left: rect.left };

  return createPortal(
    <div
      className="fixed inset-0 z-[80] pointer-events-none"
      data-testid="dashboard-card-tour"
    >
      {/* Dim scrim */}
      <div className="absolute inset-0 bg-black/55 pointer-events-auto" onClick={finish} />

      {/* Spotlight cut-out (using CSS mask via inset shadow trick) */}
      <div
        className="absolute rounded-2xl pointer-events-none transition-all duration-300"
        style={{
          top: rect.top - window.scrollY - 6,
          left: rect.left - window.scrollX - 6,
          width: rect.width + 12,
          height: rect.height + 12,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.55), 0 0 0 3px #F59E0B, 0 0 40px 6px rgba(245,158,11,0.6)',
        }}
      />

      {/* Description bubble */}
      <div
        className="absolute w-72 rounded-2xl bg-white shadow-2xl border-2 border-amber-400 p-4 pointer-events-auto"
        style={{
          top: bubbleStyle.top - window.scrollY,
          left: Math.min(Math.max(bubbleStyle.left - window.scrollX, 16), window.innerWidth - 304),
        }}
        data-testid="dashboard-card-tour-bubble"
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-1.5">
            <Sparkles size={14} className="text-amber-500" />
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
              Step {step + 1} of {cardOrder.length}
            </div>
          </div>
          <button
            type="button"
            onClick={finish}
            aria-label="Skip tour"
            className="p-1 rounded hover:bg-gray-100"
            data-testid="dashboard-card-tour-skip"
          >
            <X size={12} className="text-gray-500" />
          </button>
        </div>
        <div className="text-sm font-bold text-gray-900 mb-1">{meta.title}</div>
        <p className="text-xs text-gray-700 leading-relaxed mb-3">{meta.description}</p>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={finish}
            className="text-[11px] font-semibold text-gray-500 hover:text-gray-800"
            data-testid="dashboard-card-tour-skip-text"
          >
            Skip tour
          </button>
          <button
            type="button"
            onClick={() => (isLast ? finish() : setStep(step + 1))}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold shadow"
            data-testid="dashboard-card-tour-next"
          >
            {isLast ? 'Done' : 'Next'} <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DashboardCardTour;
